/**
 * Display-only formatting for the supervisor time UI.
 *
 * IMPORTANT: none of this is payroll math. Authoritative hours come from the
 * shared pure `computeHours()` on the server (rounding rule / grace / overnight
 * are Dev-A's contract). `elapsedHours` here is a raw wall-clock duration shown
 * only as a supervisor convenience and must never drive a posted figure.
 */

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

/** Format an ISO instant as a local date, e.g. `Aug 04, 2026`. Empty on invalid. */
export function formatDay(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') {
    return '';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DATE_FMT.format(d);
}

/**
 * Short local date, e.g. `Sep 09` — no year.
 *
 * For a phone timecard: every row in a view sits inside ONE pay period, so the
 * year is the same on all of them and costs ~40px per row, which is what pushed
 * the Status column off the right edge.
 */
const DATE_FMT_SHORT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
});

export function formatDayShort(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') {
    return '';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DATE_FMT_SHORT.format(d);
}

/** Format an ISO instant as a local time, e.g. `07:45`. `—` when open/unset. */
export function formatClock(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : TIME_FMT.format(d);
}

/**
 * Raw wall-clock hours between two ISO instants, to 2 decimals, for display
 * only. Returns `null` for an open session (no clock-out) or invalid input.
 */
export function elapsedHours(
  clockInAt: string | null | undefined,
  clockOutAt: string | null | undefined
): number | null {
  if (
    clockInAt === null ||
    clockInAt === undefined ||
    clockInAt === '' ||
    clockOutAt === null ||
    clockOutAt === undefined ||
    clockOutAt === ''
  ) {
    return null;
  }
  const start = new Date(clockInAt).getTime();
  const end = new Date(clockOutAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return null;
  }
  return Math.round(((end - start) / 3_600_000) * 100) / 100;
}

/**
 * Convert an ISO instant to the `YYYY-MM-DDTHH:mm` shape that a native
 * datetime-local input requires (in LOCAL time). Empty string when unset.
 */
export function toLocalInputValue(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/**
 * Convert a `datetime-local` input value (local wall time) back to an ISO-8601
 * UTC instant for the API. Returns `null` for an empty/invalid value.
 */
export function fromLocalInputValue(value: string): string | null {
  if (value === '') {
    return null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
